<template>
  <div class="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
    <!-- Header Section -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
      <h2 class="text-xl font-semibold text-gray-800 flex items-center gap-2">
        <i class="ri-team-line text-blue-600 text-2xl"></i>
        Users List
      </h2>

      <!-- Search -->
      <div class="mt-3 sm:mt-0">
        <input
          v-model="search"
          type="text"
          placeholder="Search by name or email..."
          class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
        />
      </div>
    </div>

    <!-- 🌀 Loading Spinner -->
    <div v-if="isLoading" class="flex justify-center py-10">
      <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600"></div>
    </div>

    <!-- Table Section -->
    <div v-else class="overflow-x-auto border border-gray-100 rounded-xl">
      <table class="min-w-full text-left text-sm text-gray-700">
        <thead class="bg-gray-50 text-gray-600 text-sm uppercase font-medium">
          <tr>
            <th class="px-6 py-3">Name</th>
            <th class="px-6 py-3">Email</th>
            <th class="px-6 py-3">Mobile</th>
            <th class="px-6 py-3">Plan Name</th>
            <th class="px-6 py-3 text-center">Action</th>
          </tr>
        </thead>

        <tbody class="divide-y divide-gray-100">
          <tr
            v-for="(user, index) in paginatedUsers"
            :key="index"
            class="hover:bg-blue-50/40 transition"
          >
            <td class="px-6 py-4 font-medium text-gray-900">{{ user.name }}</td>
            <td class="px-6 py-4">{{ user.email }}</td>
            <td class="px-6 py-4">{{ user.mobile }}</td>
            <td class="px-6 py-4">{{ user.user_plan_name }}</td>
            <td class="px-6 py-4 text-center">
              <button
                class="text-blue-600 hover:text-blue-800 font-medium"
                @click="viewUser(user)"
              >
                <i class="ri-eye-line mr-1"></i> View
              </button>
              <button
                class="text-blue-600 hover:text-blue-800 font-medium"
                 @click="openDeleteDialog(user)"
              >
                <i class="ri-eye-line mr-1"></i> Delete
              </button>
            </td>
          </tr>

          <tr v-if="paginatedUsers.length === 0">
            <td colspan="5" class="text-center text-gray-500 py-6">
              No users found
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="flex items-center justify-between mt-6" v-if="!isLoading && totalRecords > 0">
      <p class="text-sm text-gray-500">
        Showing
        <span class="font-semibold text-gray-700">{{ startIndex + 1 }}</span>
        -
        <span class="font-semibold text-gray-700">{{ endIndex }}</span>
        of
        <span class="font-semibold text-gray-700">{{ totalRecords }}</span>
        users
      </p>

      <div class="flex gap-2">
        <button
          @click="prevPage"
          :disabled="page === 1"
          class="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50"
        >
          <i class="ri-arrow-left-s-line"></i>
        </button>
        <button
          @click="nextPage"
          :disabled="endIndex >= totalRecords"
          class="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50"
        >
          <i class="ri-arrow-right-s-line"></i>
        </button>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
<div
  v-if="showDeleteDialog"
  class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
>
  <div class="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
    <h3 class="text-lg font-semibold text-gray-800 mb-2">
      Confirm Delete
    </h3>

    <p class="text-sm text-gray-600 mb-6">
      Are you sure you want to permanently delete
      <span class="font-semibold text-gray-800">
        {{ selectedUser?.name }}
      </span>
      ?
      <br />
      This action cannot be undone.
    </p>

    <div class="flex justify-end gap-3">
      <button
        @click="closeDeleteDialog"
        :disabled="isDeleting"
        class="px-4 py-2 rounded-lg border text-gray-600 hover:bg-gray-100"
      >
        Cancel
      </button>

      <button
        @click="confirmDelete"
        :disabled="isDeleting"
        class="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center gap-2"
      >
        <span v-if="!isDeleting">Delete</span>
        <span v-else class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
      </button>
    </div>
  </div>
</div>

  </div>
</template>

<script>
import { getUsrList,delUser } from "@/apis/webapis";

export default {
  name: "UsersList",
  data() {
    return {
      search: "",
      page: 1,
      perPage: 10,
      users: [],
      totalRecords: 0,
      isLoading: false,
      showDeleteDialog: false,
      selectedUser: null,
      isDeleting: false,
    };
  },
  computed: {
    filteredUsers() {
      const term = this.search.toLowerCase();
      return this.users.filter(
        (u) =>
          u.name?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term)
      );
    },
    startIndex() {
      return (this.page - 1) * this.perPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.perPage, this.filteredUsers.length);
    },
    paginatedUsers() {
      return this.filteredUsers.slice(this.startIndex, this.endIndex);
    },
  },
  methods: {
    openDeleteDialog(user) {
      this.selectedUser = user;
      this.showDeleteDialog = true;
    },

    closeDeleteDialog() {
      if (this.isDeleting) return;
      this.showDeleteDialog = false;
      this.selectedUser = null;
    },
    async confirmDelete() {
    if (!this.selectedUser) return;

    try {
      this.isDeleting = true;

      const response = await delUser(this.selectedUser.user_id);
       this.isDeleting = false;
      if (response.status) {
        // remove from UI instantly
        this.fetchUsers();

        this.closeDeleteDialog();
      }
    } catch (error) {
      console.error("Delete error:", error);
      // alert("Something went wrong while deleting");
    } finally {
      this.isDeleting = false;
    }
  },
    async fetchUsers() {
      try {
        this.isLoading = true;
        const response = await getUsrList(this.page - 1, this.perPage, "user", "0");

        if (response.status) {
          this.users = response.records;
          this.totalRecords = parseInt(response.total1 || "0");
        } else {
          this.users = [];
          this.totalRecords = 0;
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        this.users = [];
        this.totalRecords = 0;
      } finally {
        this.isLoading = false;
      }
    },
    nextPage() {
      if (this.endIndex < this.totalRecords) {
        this.page++;
        this.fetchUsers();
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page--;
        this.fetchUsers();
      }
    },
    viewUser(user) {
      alert(`Viewing details for ${user.name}`);
    },
  },
  created() {
    this.fetchUsers();
  },
};
</script>
